// Copyright (c) 2020, Picsa Advisory Services (Pty) Ltd. All rights reserved.

import React from 'react'
import PropTypes from 'prop-types'
import { graphql, compose } from 'react-apollo'
import gql from 'graphql-tag'
import { default as fragments } from '../../../fragments'
import { withRouter } from 'react-router-dom'

import {
  SvgIcon,
  Table,
  Spinner,
  PageNavigationButtons
} from '../../'

import { MessageTableItem } from './MessageTableItem'

import {
  decodeBase64,
  PageNavigationControl,
  getPageCursor
} from '../../../utils'


class EmployerSMSMessages extends React.Component {
  static propTypes = {
    employerId: PropTypes.string,
    status: PropTypes.string,
    direction: PropTypes.string,
    messageType: PropTypes.string,
    fromAddr: PropTypes.string,
    toAddr: PropTypes.string,
    scheduledAtFrom: PropTypes.string,
    scheduledAtTo: PropTypes.string,
    timestampFrom: PropTypes.string,
    timestampTo: PropTypes.string,
    unknownParticipant: PropTypes.bool,
    orderBy: PropTypes.string,
    changeSort: PropTypes.func,
    resultsPerPage: PropTypes.number,
    className: PropTypes.string
  }

  static defaultProps = {
    resultsPerPage: 15
  }

  state = {
    selectedMessage: null,
    replyToMessage: false,
    filterChanged: false,
    pageNav: new PageNavigationControl()
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if ((this.props.status !== nextProps.status ||
      this.props.direction !== nextProps.direction ||
      this.props.messageType !== nextProps.messageType ||
      this.props.fromAddr !== nextProps.fromAddr ||
      this.props.toAddr !== nextProps.toAddr ||
      this.props.scheduledAtFromDate !== nextProps.scheduledAtFromDate ||
      this.props.scheduledAtToDate !== nextProps.scheduledAtToDate ||
      this.props.timestampFromDate !== nextProps.timestampFromDate ||
      this.props.timestampToDate !== nextProps.timestampToDate ||
      this.props.orderBy !== nextProps.orderBy) &&
      !sessionStorage.getItem('messageListPageState')
    ) {
      let previousFilters = JSON.parse(sessionStorage.getItem('messageListFilters'))
      if (previousFilters && (
        previousFilters.status === nextProps.status ||
        previousFilters.direction === nextProps.direction ||
        previousFilters.messageType === nextProps.messageType ||
        this.props.fromAddr !== nextProps.fromAddr ||
        this.props.toAddr !== nextProps.toAddr ||
        previousFilters.scheduledAtFromDate === nextProps.scheduledAtFromDate ||
        previousFilters.scheduledAtToDate === nextProps.scheduledAtToDate ||
        this.props.timestampFromDate !== nextProps.timestampFromDate ||
        this.props.timestampToDate !== nextProps.timestampToDate ||
        this.props.orderBy !== nextProps.orderBy
      )) {
        sessionStorage.removeItem('messageListFilters')
      } else {
        this.state.pageNav.reset()
        this.setState({
          filterChanged: true
        }, () => this.props.refetch())
        sessionStorage.removeItem('messageListFilters')
      }
    } else {
      if (!nextProps.loading) {
        if (this.state.filterChanged) {
          this.setState({
            filterChanged: false
          })
          this.state.pageNav.setPageStates(null, nextProps.messages)
        }
        else if (nextProps.messages) {
          let messageListPageState = JSON.parse(sessionStorage.getItem('messageListPageState'))
          if (messageListPageState) {
            this.state.pageNav.setPageStates(null, null, messageListPageState)
            sessionStorage.removeItem('messageListPageState')
          } else {
            this.state.pageNav.setPageStates(this.props.messages, nextProps.messages)
          }
        }
      }
    }
  }

  getTableRows = (messages) => (
    messages.map(({ node: message }) => (
      <MessageTableItem
        message={message}
        identity={this.props.identity}
        actionColumn={this.props.actionColumn}
        expandMessage={this.state.selectedMessage === message.id}
        onClick={messageId => (
          this.props.history.push(`/employer/${this.props.match.params.employerId}/communications/messages/${messageId}`)
        )}
        messageIdentity={message.direction === 'INBOUND' && message.fromIdentity !== null
          ? message.fromIdentity
          : message.direction === 'OUTBOUND' && message.toIdentity !== null
            ? message.toIdentity
            : null
        }
        key={`message_${message.id}`} />
    ))
  )

  render() {
    if (this.props.loading) {
      return <Spinner className='slds-is-relative' />
    }

    if (this.props.error) {
      return <h1>Oh dear! We have an issue</h1>
    }

    let pageNav = this.state.pageNav
    let columns = ['Identity', 'Direction', 'Status', 'Date/Time', 'Type', 'Message']
    if (this.props.actionColumn) {
      columns.push(this.props.actionColumn.name)
    }

    let messages = this.props.messages
    let startCursor = messages.pageInfo.startCursor
    let totalCount = messages.totalCount

    return (
      <div className={this.props.className ? this.props.className : ''}>
        {messages.edges.length === 0
          ? <div className='slds-p-around_medium'>
              <SvgIcon
                variant='utility'
                symbol='info'
                size='small'
                colour='light'
                className='slds-m-right_medium'
              />
              {`No ${this.props.direction} messages.`}
            </div>
          : <React.Fragment>
              <Table
                columns={columns}
                rows={this.getTableRows(messages.edges)}
                objectRows={true}
                orderBy={this.props.orderBy}
                changeSort={this.props.changeSort}
                querySortableColumns={{
                  'Date/Time': {
                    orderByName: 'timestamp'
                  }
                }}
              />
              <PageNavigationButtons
                onNextClick={() => pageNav.nextPage(this.props.fetchNext)}
                onPreviousClick={() => pageNav.previousPage(this.props.fetchPrevious)}
                previousDisabled={pageNav.firstPage}
                nextDisabled={pageNav.lastPage}
                startCursor={startCursor}
                totalCount={totalCount}
                resultsPerPage={this.props.resultsPerPage}
                resultsText='messages'
                onPageNumberClick={pageNumber => this.props.refetch({ cursor: getPageCursor(pageNumber, this.props.resultsPerPage) })}
                className="slds-p-around_medium slds-float_right"
              />
            </React.Fragment>
        }
      </div>
    )
  }
}

const getMessages = gql`
  query($status: String, $direction: String, $messageType: String, $fromAddr: String, $toAddr: String, $timestampFrom: DateTime, $timestampTo: DateTime, $scheduledAtFrom: DateTime, $scheduledAtTo: DateTime, $fromIdentityId: UUID, $toIdentityId: UUID, $unknownSender: Boolean, $unknownReceiver: Boolean, $orderBy: String, $resultsPerPage: Int!, $cursor: String) {
    messages(status: $status, direction: $direction, messageType: $messageType, fromAddr_Icontains: $fromAddr, toAddr_Icontains: $toAddr, timestamp_Gte: $timestampFrom, timestamp_Lte: $timestampTo, scheduledAt_Gte: $scheduledAtFrom, scheduledAt_Lte: $scheduledAtTo, toIdentity_Id: $toIdentityId, fromIdentity_Id: $fromIdentityId, fromIdentity_Id_Isnull: $unknownSender, toIdentity_Id_Isnull: $unknownReceiver, orderBy: $orderBy, first: $resultsPerPage, after: $cursor) {
      totalCount
      edges {
        node {
          ...Message
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
  ${fragments.message}
`

const getPreviousMessages = gql`
  query($status: String, $direction: String, $messageType: String, $fromAddr: String, $toAddr: String, $timestampFrom: DateTime, $timestampTo: DateTime, $scheduledAtFrom: DateTime, $scheduledAtTo: DateTime, $fromIdentityId: UUID, $toIdentityId: UUID, $unknownSender: Boolean, $unknownReceiver: Boolean, $orderBy: String, $resultsPerPage: Int!, $cursor: String) {
    messages(status: $status, direction: $direction, messageType: $messageType, fromAddr_Icontains: $fromAddr, toAddr_Icontains: $toAddr, timestamp_Gte: $timestampFrom, timestamp_Lte: $timestampTo, scheduledAt_Gte: $scheduledAtFrom, scheduledAt_Lte: $scheduledAtTo, toIdentity_Id: $toIdentityId, fromIdentity_Id: $fromIdentityId, fromIdentity_Id_Isnull: $unknownSender, toIdentity_Id_Isnull: $unknownReceiver, orderBy: $orderBy, last: $resultsPerPage, before: $cursor) {
      totalCount
      edges {
        node {
          ...Message
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
  ${fragments.message}
`

const setUnknownParticipantQueryFilter = (unknownParticipant, direction, senderFilter) => {
  // 'directionCorrespondsToFilter', if applied to 'unknownSender' query filter, will be true if
  //    message direction is inbound
  // 'directionCorrespondsToFilter', if applied to 'unknownReceiver' query filter, will be true
  //    if message direction is outbound
  const directionCorrespondsToFilter = senderFilter
    ? direction === 'inbound'
    : direction === 'outbound'  // receiver

  return unknownParticipant && directionCorrespondsToFilter
   ? true
   : directionCorrespondsToFilter
     ? false
     : undefined
}

const withData = graphql(getMessages, {
  options: ({ status, direction, messageType, fromAddr, toAddr, timestampFrom, timestampTo, scheduledAtFrom, scheduledAtTo, fromIdentityId, toIdentityId, unknownParticipant, resultsPerPage = 15, orderBy }) => ({
    variables: {
      status,
      direction,
      messageType,
      fromAddr,
      toAddr,
      timestampFrom,
      timestampTo,
      scheduledAtFrom,
      scheduledAtTo,
      fromIdentityId: fromIdentityId ? decodeBase64(fromIdentityId) : null,
      toIdentityId: toIdentityId ? decodeBase64(toIdentityId) : null,
      unknownSender: setUnknownParticipantQueryFilter(unknownParticipant, direction, true),
      unknownReceiver: setUnknownParticipantQueryFilter(unknownParticipant, direction, false),
      resultsPerPage,
      orderBy
    },
    fetchPolicy: 'network-only'
  }),
  props: ({ ownProps: { status, direction, messageType, fromAddr, toAddr, timestampFrom, timestampTo, scheduledAtFrom, scheduledAtTo, fromIdentityId, toIdentityId, unknownParticipant, resultsPerPage = 15, orderBy }, data: { loading, error, refetch, messages, fetchMore } }) => ({
    loading,
    error,
    refetch,
    messages,
    fetchNext: () => {
      return fetchMore({
        query: getMessages,
        variables: {
          status,
          direction,
          messageType,
          fromAddr,
          toAddr,
          timestampFrom,
          timestampTo,
          scheduledAtFrom,
          scheduledAtTo,
          fromIdentityId: fromIdentityId ? decodeBase64(fromIdentityId) : null,
          toIdentityId: toIdentityId ? decodeBase64(toIdentityId) : null,
          unknownSender: setUnknownParticipantQueryFilter(unknownParticipant, direction, true),
          unknownReceiver: setUnknownParticipantQueryFilter(unknownParticipant, direction, false)
            ? true
            : undefined,
          orderBy,
          resultsPerPage,
          cursor: messages.pageInfo.endCursor
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          const newEdges = fetchMoreResult.messages.edges
          const pageInfo = fetchMoreResult.messages.pageInfo
          return {
            messages: {
              __typename: previousResult.messages.__typename,
              edges: [...newEdges],
              pageInfo
            }
          }
        }
      })
    },
    fetchPrevious: () => {
      return fetchMore({
        query: getPreviousMessages,
        variables: {
          status,
          direction,
          messageType,
          fromAddr,
          toAddr,
          timestampFrom,
          timestampTo,
          scheduledAtFrom,
          scheduledAtTo,
          fromIdentityId: fromIdentityId ? decodeBase64(fromIdentityId) : null,
          toIdentityId: toIdentityId ? decodeBase64(toIdentityId) : null,
          unknownSender: setUnknownParticipantQueryFilter(unknownParticipant, direction, true),
          unknownReceiver: setUnknownParticipantQueryFilter(unknownParticipant, direction, false),
          orderBy,
          resultsPerPage,
          cursor: messages.pageInfo.startCursor
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          const newEdges = fetchMoreResult.messages.edges
          const pageInfo = fetchMoreResult.messages.pageInfo
          return {
            messages: {
              __typename: previousResult.messages.__typename,
              edges: [...newEdges],
              pageInfo
            }
          }
        }
      })
    }
  })
})

export default compose(
  withData,
  withRouter
)(EmployerSMSMessages)



// WEBPACK FOOTER //
// ./src/components/messageplus/employer-sms-messages/index.js